import { useState, useCallback } from 'react';
import { Checkbox } from '@client/components/ui/checkbox';
import { Button } from '@client/components/ui/button';
import { getUserIpAddress } from '@/util/getUserIpAddress';

interface AdvisorHierarchyConsentDialogProps {
  supervisorName: string;
  supervisorOrgName: string | null;
  enabledClientAccess: boolean;
  onAccept: (consentData: ConsentData) => void;
  onDecline: () => void;
  isSubmitting?: boolean;
}

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

export function AdvisorHierarchyConsentDialog({
  supervisorName,
  supervisorOrgName,
  enabledClientAccess,
  onAccept,
  onDecline,
  isSubmitting = false,
}: AdvisorHierarchyConsentDialogProps) {
  const [hasConsented, setHasConsented] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!hasConsented) return;

    let ipAddress: string | null = null;
    try {
      ipAddress = await getUserIpAddress();
    } catch (error) {
      console.error('Failed to retrieve IP address:', error);
    }

    // Capture consent metadata
    const consentData: ConsentData = {
      hasConsented: true,
      ipAddress,
      userAgent: navigator.userAgent,
    };

    onAccept(consentData);
  }, [hasConsented, onAccept]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-semibold text-gray-900">Accept Invitation</h3>
        <p className="mt-1 text-gray-700">
          Review and accept the invitation to join this organization
        </p>
      </div>

      {/* Organization Details - Simple two-column layout */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Organization</span>
          <span className="font-medium text-gray-900">{supervisorOrgName || 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Owner</span>
          <span className="font-medium text-gray-900">{supervisorName}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-700">Client Access</span>
          <span className="font-medium text-gray-900">
            {enabledClientAccess ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {/* Consent Description */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        <p className="text-sm text-blue-700">
          {enabledClientAccess
            ? 'By accepting this invitation, I agree to join the specified organization and grant the organization owner permission to view my subscription and usage statistics, as well as view and edit my clients and their information. I understand I may revoke this access at any time through my account settings.'
            : 'By accepting this invitation, I agree to join the specified organization and grant the organization owner permission to view my subscription and usage statistics. I understand I may revoke this access at any time through my account settings.'}
        </p>
      </div>

      {/* Consent Checkbox */}
      <div className="flex items-center gap-3">
        <Checkbox
          id="hierarchy-consent"
          checked={hasConsented}
          onCheckedChange={checked => setHasConsented(checked === true)}
          disabled={isSubmitting}
          className="border-primary data-[state=checked]:bg-primary h-5 w-5 rounded data-[state=checked]:text-white"
        />
        <label
          htmlFor="hierarchy-consent"
          className="cursor-pointer text-sm text-gray-700"
        >
          {enabledClientAccess
            ? 'I have read, understood, and agree to join this organization and grant the specified client access permissions.'
            : 'I have read, understood, and agree to join this organization with the specified permissions.'}
        </label>
      </div>

      {/* Action Button - Only Accept & Join */}
      <div className="flex justify-center pt-2">
        <Button
          onClick={handleAccept}
          disabled={!hasConsented || isSubmitting}
          className="bg-primary w-full px-8 hover:bg-purple-700"
        >
          {isSubmitting ? 'Processing...' : 'Accept & Join'}
        </Button>
      </div>
    </div>
  );
}
